import NoneChip from '@/components/chip/none-chip';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import InlineToast from '@/components/toasts/inline';
import { JsonObject } from '@/lib/helpers';
import { FC, useMemo } from 'react';
import { TelemetryModel } from '@/lib/models/telemetry/telemetry.model';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { Stack } from '@mui/material';
import {
  AwsS3TelemetryConfig,
  AwsS3TelemetryModel,
} from '@/lib/models/telemetry/aws-s3-telemetry.model';
import { TelemetrySettings } from '@/lib/services/telemetry.service';
import TextInput from '@/components/form/text-input';
import ConnectorLink from '@/components/connector-link';
import {
  HttpTelemetryConfig,
  HttpTelemetryModel,
} from '@/lib/models/telemetry/http-telemetry.model';
import { getHttpAuthTypeOptions } from '@/lib/models/telemetry';
import TelemetrySourceCard from './telemetry-source-card';
import { SplunkTelemetryConfig } from '@/lib/models/telemetry/splunk-telemetry.model';
import { CrowdStrikeTelemetryConfig } from '@/lib/models/telemetry/crowdstrike-telemetry.model';
import { DatadogTelemetryConfig } from '@/lib/models/telemetry/datadog-telemetry.model';

interface Props {
  telemetry: TelemetryModel;
  settings: TelemetrySettings;
  connector?: ConnectorModel;
}

const TelemetryReadonlyView: FC<Props> = ({ connector, telemetry, settings }) => {
  const [data, fields] = useMemo(() => {
    const fields: ReadonlyField[] = [
      { label: 'Name', prop: 'name' },
      { label: 'Type', prop: 'type' },
      { label: 'Activation', prop: 'enabled' },
      { label: 'Status', prop: 'status' },
      { label: 'Status Message', prop: 'statusMessage' },
      { label: 'Telemetry Sources', prop: 'telemetryTypes' },
      { label: 'Last Synced Date', prop: 'lastSyncedAt' },
      { label: 'Configuration', prop: 'config-section', propType: 'section' },
    ];

    const { displayName, typeLabel, enabled, status, statusMessage, config } = telemetry;

    const data: JsonObject = {
      name: displayName,
      type: typeLabel,
      status: <StatusChip value={status as ChipStatus} size="small" />,
      enabled: <StatusChip value={enabled} size="small" />,
      telemetryTypes: (
        <Stack gap={1}>
          <TelemetrySourceCard telemetry={telemetry} sourceType="userEvents" />
          <TelemetrySourceCard telemetry={telemetry} sourceType="auditEvents" />
        </Stack>
      ),
      statusMessage: statusMessage ? (
        <InlineToast hideIcon message={statusMessage} level="error" />
      ) : (
        <NoneChip />
      ),
    };

    switch (telemetry.type) {
      case 'splunk':
        {
          const { hecUrl, hecTokenLast4, source, allowSelfSignedCertificates, indexes } =
            config as SplunkTelemetryConfig;
          const { userEvents: userEventIndex, auditEvents: auditEventIndex } = indexes;

          fields.push({ label: 'HEC URL', prop: 'hecUrl' });
          data.hecUrl = (
            <TextInput size="small" name="config.hecUrl" value={hecUrl} readonly copyClip />
          );

          fields.push({ label: 'Source Identifier', prop: 'source' });
          data.source = source;

          const hasHecTokenPreview = !!hecTokenLast4;

          fields.push({
            label: 'HEC Token',
            prop: 'hecTokenLast4',
          });
          data.hecTokenLast4 = hasHecTokenPreview ? (
            <NoneChip redact length={32} hint={hecTokenLast4} />
          ) : (
            <NoneChip notAvailable />
          );

          fields.push({ label: 'User Event Index', prop: 'userEventIndex' });
          data.userEventIndex = userEventIndex || <NoneChip notAvailable />;

          fields.push({ label: 'Audit Event Index', prop: 'auditEventIndex' });
          data.auditEventIndex = auditEventIndex || <NoneChip notAvailable />;

          fields.push({
            label: 'Allow Self-signed Certificates',
            prop: 'allowSelfSignedCertificates',
          });
          data.allowSelfSignedCertificates = (
            <StatusChip value={allowSelfSignedCertificates} size="small" />
          );
        }
        break;

      case 'crowdstrike':
        {
          const { hecUrl, hecTokenLast4, source, allowSelfSignedCertificates, indexes } =
            config as CrowdStrikeTelemetryConfig;
          const { userEvents: userEventIndex, auditEvents: auditEventIndex } = indexes;

          fields.push({ label: 'HEC URL', prop: 'hecUrl' });
          data.hecUrl = (
            <TextInput size="small" name="config.hecUrl" value={hecUrl} readonly copyClip />
          );

          fields.push({ label: 'Source Identifier', prop: 'source' });
          data.source = source;

          const hasHecTokenPreview = !!hecTokenLast4;

          fields.push({
            label: 'HEC Token',
            prop: 'hecTokenLast4',
          });
          data.hecTokenLast4 = hasHecTokenPreview ? (
            <NoneChip redact length={32} hint={hecTokenLast4} />
          ) : (
            <NoneChip notAvailable />
          );

          fields.push({ label: 'User Event Index', prop: 'userEventIndex' });
          data.userEventIndex = userEventIndex || <NoneChip notAvailable />;

          fields.push({ label: 'Audit Event Index', prop: 'auditEventIndex' });
          data.auditEventIndex = auditEventIndex || <NoneChip notAvailable />;

          fields.push({
            label: 'Allow Self-signed Certificates',
            prop: 'allowSelfSignedCertificates',
          });
          data.allowSelfSignedCertificates = (
            <StatusChip value={allowSelfSignedCertificates} size="small" />
          );
        }
        break;

      case 'datadog':
        {
          const { apiKeyLast4, site, ddsource, ddtags, service, hostname } =
            config as DatadogTelemetryConfig;
          const hasApiKeyPreview = !!apiKeyLast4;

          fields.push({ label: 'Datadog Site', prop: 'site' });
          data.site = site || <NoneChip notAvailable />;

          fields.push({ label: 'API Key', prop: 'apiKeyLast4' });
          data.apiKeyLast4 = hasApiKeyPreview ? (
            <NoneChip redact length={32} hint={apiKeyLast4} />
          ) : (
            <NoneChip notAvailable />
          );

          fields.push({ label: 'Source', prop: 'ddsource' });
          data.ddsource = ddsource || <NoneChip notAvailable />;

          fields.push({ label: 'Tags', prop: 'ddtags' });
          data.ddtags = ddtags || <NoneChip notAvailable />;

          fields.push({ label: 'Service', prop: 'service' });
          data.service = service || <NoneChip notAvailable />;

          fields.push({ label: 'Hostname', prop: 'hostname' });
          data.hostname = hostname || <NoneChip notAvailable />;
        }
        break;

      case 'http':
        {
          const { usesAuthHeader } = telemetry as HttpTelemetryModel;
          const {
            url,
            authType,
            authHeaderName,
            authTokenLast4,
            allowSelfSignedCertificates,
            compress,
            format,
          } = config as HttpTelemetryConfig;

          const authTypeLabel =
            getHttpAuthTypeOptions().find(({ value }) => value === authType)?.label || 'Unknown';
          const hasAuthTokenPreview = !!authTokenLast4;

          fields.push({ label: 'Endpoint URL', prop: 'url' });
          data.url = <TextInput size="small" name="config.url" value={url} readonly copyClip />;

          fields.push({ label: 'Authentication Type', prop: 'authType' });
          data.authType = authTypeLabel;

          fields.push({ label: 'Authentication Header Name', prop: 'authHeaderName' });
          data.authHeaderName = authHeaderName;

          fields.push({
            label: usesAuthHeader ? 'API Key' : 'Bearer Token',
            prop: 'authTokenLast4',
          });
          data.authTokenLast4 = hasAuthTokenPreview ? (
            <NoneChip redact length={32} hint={authTokenLast4} />
          ) : (
            <NoneChip notAvailable />
          );

          fields.push({
            label: 'Allow Self-signed Certificates',
            prop: 'allowSelfSignedCertificates',
          });
          data.allowSelfSignedCertificates = (
            <StatusChip value={allowSelfSignedCertificates} size="small" />
          );

          fields.push({
            label: 'Gzip Request Body',
            prop: 'compress',
          });
          data.compress = <StatusChip value={compress} size="small" />;

          fields.push({ label: 'Format', prop: 'format' });
          data.format = format;
        }
        break;

      case 's3':
        {
          const { bucketName, bucketRegion, keyPrefix } = config as AwsS3TelemetryConfig;

          const hasConnector = !!connector;

          fields.push({ label: 'Connector', prop: 'connector' });
          data.connector = hasConnector ? (
            <ConnectorLink connector={connector} variant="outlined" showType />
          ) : (
            <NoneChip notAvailable />
          );

          fields.push({ label: 'AWS S3 Bucket Name', prop: 'bucketName' });
          data.bucketName = bucketName;

          fields.push({ label: 'AWS S3 Key Prefix', prop: 'keyPrefix' });
          data.keyPrefix = keyPrefix || <NoneChip />;

          fields.push({ label: 'AWS S3 Destination URLs', prop: 'destinationUrl' });
          data.destinationUrl = (
            <Stack gap={0.5}>
              <TextInput
                size="small"
                name="destinationUrl"
                value={(telemetry as AwsS3TelemetryModel).getDestinationUrl(settings, 'user-event')}
                readonly
                copyClip
              />
              <TextInput
                size="small"
                name="destinationUrl"
                value={(telemetry as AwsS3TelemetryModel).getDestinationUrl(
                  settings,
                  'audit-event'
                )}
                readonly
                copyClip
              />
            </Stack>
          );

          fields.push({ label: 'AWS Region', prop: 'bucketRegion' });
          data.bucketRegion = bucketRegion;
        }
        break;
    }

    return [data, fields];
  }, [connector, telemetry, settings]);

  return <ReadonlyView fields={fields} data={data} />;
};

export default TelemetryReadonlyView;
